/**
 * Status badge — small, consistent label for state.
 *
 * The audit found pages mixing raw Tailwind palette colors
 * (`bg-red-100 text-red-700`, `bg-emerald-100`, …) with the design
 * tokens (success / warning / destructive / info / muted). This
 * component is the single source of truth.
 *
 *   <StatusBadge tone="success">Completed</StatusBadge>
 *   <StatusBadge tone="warning" icon={AlertTriangle}>Backlog</StatusBadge>
 */
import React from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";


export type StatusTone = "success" | "warning" | "destructive" | "info" | "neutral";


export interface StatusBadgeProps {
  tone?: StatusTone;
  icon?: LucideIcon;
  children: React.ReactNode;
  className?: string;
}


const TONE_CLASSES: Record<StatusTone, string> = {
  // All foreground/background combos go through tokens so dark mode +
  // brand changes stay in lock-step with index.css.
  success: "bg-success/10 text-success border-success/20",
  warning: "bg-warning/10 text-warning border-warning/20",
  destructive: "bg-destructive/10 text-destructive border-destructive/20",
  info: "bg-info/10 text-info border-info/20",
  neutral: "bg-muted text-muted-foreground border-border",
};


export function StatusBadge({
  tone = "neutral", icon: Icon, children, className,
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        TONE_CLASSES[tone],
        className,
      )}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {children}
    </span>
  );
}
